/**
 * SendBundle renderer · iter303aj (fix22 · iter303ak — Task 40 aware)
 *
 * MOCKED send-and-stamp step for any status pipeline.
 *
 * Previously hard-coded to Appraisal Booked (channels = notify_channels,
 * letters = auth + confirmation). Now reads letter_keys, message_key,
 * stamp_field and channels-source from action.params so the same
 * renderer serves both Appraisal Booked and Quote Sent (and future
 * pipelines).
 *
 * action.params keys honoured:
 *   • letter_keys       — comma-separated letter keys to attach
 *   • message_key       — SMS message key (used in the audit log)
 *   • stamp_field       — appraisal field to stamp with sent_at ISO
 *                         (default: client_notified_at)
 *   • write_to          — captured_data key to write result under
 *   • prompt            — banner text
 *   • channels_source   — captured_data key to read channels from
 *                         (default: sniffs quote_notify_channels then
 *                          notify_channels)
 *
 * Once SMTP + SMS credentials land, this switches from mock → real send.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Send, CheckCircle2, Mail, MessageSquare, Printer, AlertTriangle, Loader2, Download } from 'lucide-react';
import SaveDocButton from '../../../components/SaveDocButton';
import { sendWorkflowEmail } from './_emailSend';
import { termsDocsFor, useQuoteTermsMode, QuoteTermsPicker } from './_quoteTerms';
import { EMAIL_FORMAT } from '@/components/EmailCheckHint';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`, withCredentials: true });

function emailSubject(keys) {
  if (keys.some(k => k === 'quotation_letter' || k === 'quote_sent')) return 'Your quotation from Roofs To Remedy Ltd';
  if (keys.some(k => k.startsWith('appraisal_'))) return 'Your roof appraisal booking – Roofs To Remedy Ltd';
  if (keys.some(k => k.startsWith('accepted_'))) return 'Thank you for accepting your quote – Roofs To Remedy Ltd';
  return 'A letter from Roofs To Remedy Ltd';
}

// iter303ak-fix22 · letter_key → { pdf route template, human label }.
// Route template contains `{appraisal_id}` which is expanded at click
// time. Falls back to a plain `/reports/status-letter/{key}/{id}` for
// unknown keys so a newly-seeded studio letter still renders without a
// code change.
const LETTER_ROUTES = {
  appraisal_booked:        { route: '/reports/appraisal-authorisation/{appraisal_id}', label: 'Auth (Appraisal Booked)' },
  appraisal_confirmation:  { route: '/reports/appraisal-confirmation/{appraisal_id}',  label: 'Booking Confirmation' },
  quote_sent:              { route: '/reports/status-letter/quote_sent/{appraisal_id}', label: 'Cover Letter' },
  quotation_letter:        { route: '/reports/quotation-detailed/{appraisal_id}',       label: 'Quotation Report' },
};

function resolveLetter(key) {
  const trim = (key || '').trim();
  if (!trim) return null;
  const cfg = LETTER_ROUTES[trim];
  if (cfg) return { key: trim, ...cfg };
  // Fallback: assume studio letter served by generic status-letter route
  return { key: trim, route: `/reports/status-letter/${trim}/{appraisal_id}`, label: trim };
}

// iter303ak-fix23j · Dynamic letter-key selector for the Accepted T20
// send. Delegates to the shared helper (Batch B, 2026-02) so the
// branching is Jest-testable and stays in lock-step with LetterPreview.
// Returns '' instead of null for the SendBundle "no key" case since
// this path builds a comma-separated key list.
// Batch D imports `rollupDepositState` for the completeness-gate banner.
import { selectAcceptedLetterKey, rollupDepositState } from './_letterSelector';

function selectLetterKeys(selector, capturedData) {
  return selectAcceptedLetterKey(selector, capturedData, '');
}

export default function SendBundle({ action, capturedData, onNext, onBack, onCancel, canBack, onChange }) {
  const params = action.params || {};
  const client = capturedData?.client_record || {};
  const appraisalId = capturedData?.appraisal_id;

  // iter303ak-fix22 · Channels: prefer explicit source, else sniff
  // quote_notify_channels (Task 40) → notify_channels (Task 20 in
  // Appraisal Booked). Falls back to [] so the send button remains
  // disabled if operator skipped the channel picker.
  const channelsSource = params.channels_source;
  const channels = (channelsSource && capturedData?.[channelsSource])
    || capturedData?.quote_notify_channels
    || capturedData?.notify_channels
    || [];

  // iter303ak-fix23 · Letters honoured from params.letter_keys (static)
  // or params.letter_keys_selector (dynamic — resolved from state).
  const resolvedLetterKeys = params.letter_keys
    || selectLetterKeys(params.letter_keys_selector, capturedData)
    || 'appraisal_booked,appraisal_confirmation';
  const letterList = String(resolvedLetterKeys)
    .split(',')
    .map((k) => resolveLetter(k))
    .filter(Boolean);

  const isQuoteSend = letterList.some((l) => l.key === 'quotation_letter' || l.key === 'quote_sent');
  const [termsMode, setTermsMode] = useQuoteTermsMode(api, isQuoteSend, appraisalId);

  const stampField = params.stamp_field || 'client_notified_at';
  const messageKey = params.message_key || '';

  // iter303ak-fix22c · Per-child expansion for `quotation_letter`.
  // Under Parent-Child, each child appraisal has its own quote_items —
  // so the Send step must attach N Quotation PDFs (one per child) plus
  // any non-quotation letters (Cover Letter, etc.) as-is.
  const [children, setChildren] = useState([]);
  const [childrenLoaded, setChildrenLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!appraisalId) { setChildrenLoaded(true); return; }
      try {
        const { data } = await api.get(`/appraisals/${appraisalId}/children`);
        if (!cancelled) setChildren(Array.isArray(data?.children) ? data.children : []);
      } catch { if (!cancelled) setChildren([]); }
      finally { if (!cancelled) setChildrenLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [appraisalId]);

  // Expand letter list: for each `quotation_letter` in letterList,
  // replace it with N entries (one per child). Non-quotation letters
  // pass through unchanged. If no children exist, quotation_letter
  // stays as a single entry keyed on the parent id (legacy path).
  const expandedLetters = React.useMemo(() => {
    const out = [];
    for (const l of letterList) {
      if (l.key === 'quotation_letter' && children.length > 0) {
        children.forEach((c) => {
          const cid = c.id || c._id;
          const label = [c.quote_type_ref, c.variant_label].filter(Boolean).join(' · ');
          const apNo = c.appraisal_no ? `A${c.appraisal_no}` : '';
          out.push({
            key: 'quotation_letter',
            route: l.route,
            label: `Quotation · ${label || 'Quote'}${apNo ? ` · ${apNo}` : ''}`,
            appraisal_id_override: cid,
          });
        });
      } else {
        out.push(l);
      }
    }
    if (isQuoteSend) {
      // Simple Terms carry each quote's number/total/deposit → one per quote; Full goes once per job.
      termsDocsFor(termsMode).forEach((t) => {
        if (t.perQuote && children.length > 0) {
          children.forEach((c) => out.push({ ...t, label: `${t.label}${c.appraisal_no ? ` · A${c.appraisal_no}` : ''}`, appraisal_id_override: c.id || c._id }));
        } else out.push(t);
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedLetterKeys, children, isQuoteSend, termsMode]);

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  // Batch D · UX polish (Feb 2026): completeness gate. Roll up the
  // deposit state via the shared helper (same one selectLetterKeys
  // uses) so this surfacing is guaranteed consistent with the letter
  // actually about to go out. Displays a soft-warn banner above the
  // send button when a deposit is required but not yet captured —
  // Trevor still CAN send (the awaiting-deposit letter is legitimate)
  // but he sees the state upfront and can back out to capture first
  // if he intended to.
  const depositRollup = React.useMemo(
    () => rollupDepositState(capturedData),
    [capturedData],
  );
  const showAwaitingWarn = depositRollup.anyRequired && !depositRollup.allCaptured;
  const showCapturedInfo = depositRollup.anyRequired && depositRollup.allCaptured;

  // 2026-02 Document Auto-Save · derive save-to-disk doc kinds from the
  // letter keys about to go out. Batches multiple docs into a single
  // zip via the /document-archive/save endpoint. Empty → hides button.
  const saveDocConfig = React.useMemo(() => {
    const letterKeys = expandedLetters.map((l) => l.key);
    const kinds = [];
    let label = 'Save Documents';
    let groupLabel = 'Documents';
    if (letterKeys.includes('appraisal_booked') || letterKeys.includes('appraisal_confirmation')) {
      if (letterKeys.includes('appraisal_confirmation')) kinds.push('appraisal_confirmation');
      if (letterKeys.includes('appraisal_booked')) kinds.push('appraisal_authorisation');
      label = 'Save Appraisal Docs';
      groupLabel = 'Appraisal Docs';
    } else if (letterKeys.includes('quotation_letter') || letterKeys.includes('quote_sent')) {
      kinds.push('quotation_detailed');
      if (letterKeys.includes('quote_sent')) kinds.push('cover_letter');
      expandedLetters.forEach((l) => { if (l.kind && !kinds.includes(l.kind)) kinds.push(l.kind); });
      label = 'Save Quotation Pack';
      groupLabel = 'Quotation Pack';
    } else {
      // Accepted letter variants (dynamic key resolved via selector)
      const acceptedKey = letterKeys.find((k) => k && k.startsWith('accepted_'));
      if (acceptedKey) {
        kinds.push(acceptedKey);
        label = 'Save Accepted Letter';
        groupLabel = 'Accepted Letter';
      }
    }
    return { kinds, label, groupLabel };
  }, [expandedLetters]);

  const send = async () => {
    setSending(true); setErr('');
    try {
      let emailRes = null;
      if (channels.includes('email')) {
        if (!client.email) throw new Error('This client has no email address. Add one, or pick a different channel.');
        if (!EMAIL_FORMAT.test(client.email.trim())) throw new Error(`“${client.email}” isn't a valid email address. Fix it on the client record, or pick a different channel.`);
        const first = (client.name_first || '').trim();
        emailRes = await sendWorkflowEmail(api, {
          to: client.email,
          subject: emailSubject(expandedLetters.map(l => l.key)),
          body: `Kia ora${first ? ` ${first}` : ''},\n\nPlease find your documents from Roofs To Remedy Ltd attached.\n\nIf you have any questions, just reply to this email.\n\nNgā mihi,\nRoofs To Remedy Ltd`,
          attachments: expandedLetters.map(l => ({
            route: l.route.replace('{appraisal_id}', l.appraisal_id_override || appraisalId),
            filename: `${l.label.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '')}_${(client.name_last || '').replace(/[^A-Za-z0-9]+/g, '')}`.replace(/_+$/, ''),
          })),
          appraisalId, event: 'send_bundle',
        });
      }
      const nowIso = new Date().toISOString();
      const res = {
        sent_at: nowIso,
        channels,
        letter_keys: expandedLetters.map((l) => l.key),
        letter_details: expandedLetters.map((l) => ({
          key: l.key,
          label: l.label,
          appraisal_id: l.appraisal_id_override || appraisalId,
        })),
        message_key: messageKey || null,
        terms_mode: isQuoteSend ? termsMode : null,
        stamp_field: stampField,
        mock: !channels.includes('email'),
        details: {},
      };
      if (channels.includes('email')) {
        res.details.email = {
          to: client.email || '',
          letters: expandedLetters.map((l) => `${l.key} (${l.label})`),
          status: emailRes?.redirected_to ? `Sent (preview → ${emailRes.redirected_to})` : 'Sent',
        };
      }
      if (channels.includes('sms')) {
        res.details.sms = {
          to: client.phone_mobile || '',
          message_key: messageKey || null,
          status: 'MOCKED · would send once SMS gateway is configured',
        };
      }
      if (channels.includes('postal')) {
        res.details.postal = {
          address: [
            [client.address_num, client.address_street].filter(Boolean).join(' '),
            client.address_suburb, client.address_city, client.address_postcode,
          ].filter(Boolean).join(', '),
          letters: expandedLetters.map((l) => `${l.key} (${l.label})`),
          status: 'Ready to download PDFs for physical mail',
        };
      }

      // Persist result via onChange (runner writes to captured_data on advance).
      onChange && onChange(res);
      setResult(res);

      // W-B3 fix (2026-02): now that POST /workflow-audit is a real endpoint,
      // pass a `channel` + `letters` list so communications_log gets a proper
      // typed row (rather than the previous silent 404).
      if (appraisalId) {
        try {
          await api.post(`/workflow-audit`, {
            appraisal_id: appraisalId,
            event: 'send_bundle',
            channel: (channels && channels[0]) || 'email',
            letters: expandedLetters.map(l => l.key),
            note: `Sent · channels=${channels.join(',')} · letters=${expandedLetters.map(l => l.key).join(',')} · sent_at=${nowIso}`,
          });
        } catch { /* silent */ }
        // Stamp on the appraisal using the configured stamp_field
        try {
          await api.patch(`/appraisals/${appraisalId}`, {
            [stampField]: nowIso,
            notify_channels_last: channels,
          });
        } catch { /* silent */ }
      }

      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  const downloadPdf = (letter, inline = false) => {
    const targetId = (letter && letter.appraisal_id_override) || appraisalId;
    if (!targetId || !letter) return;
    const route = letter.route.replace('{appraisal_id}', targetId);
    const qs = inline ? '?inline=1' : '';
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api${route}${qs}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div data-testid="wf-send-bundle">
      <div className="bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-400 rounded-r-lg p-5 mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-600 font-semibold mb-2">
          <Send size={12} /> Send Notification Bundle
        </div>
        <div className="text-slate-700 text-sm">
          {(params.prompt || '').replace(/\s*Mocked while[^.]*\.\s*(logs[^.]*\.)?/i, ' ').trim() || 'Send the letters + SMS via the chosen channels.'}
        </div>
      </div>

      {/* Batch D · Feb 2026: consolidated deposit soft-gate banners live
          just above the send button (see below in this file). The old
          `wf-send-awaiting-deposit-warn` block that used to live here
          was retired to avoid stacking two identical warnings —
          Batch D's `wf-send-deposit-awaiting-banner` covers the same
          case with a matching emerald received-partner banner. */}

      {/* Big MOCKED banner */}
      <div className="mb-5 p-4 bg-amber-50 border-2 border-amber-300 rounded flex items-start gap-3"
        data-testid="wf-send-mocked-banner">
        <AlertTriangle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm text-amber-900">
          <div className="font-bold text-base">Email sends for real</div>
          <div className="text-xs mt-1">
            The client gets a real email with the PDFs below attached. SMS is still recorded only
            until the TNZ text-message account is connected.
          </div>
        </div>
      </div>

      {/* iter303ak-fix22 · Empty-channels guardrail so operators know
          why the Send button is disabled. */}
      {channels.length === 0 && (
        <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-800"
          data-testid="wf-send-no-channels">
          No delivery channel selected. Go back to the previous step and pick at least one channel.
        </div>
      )}

      {isQuoteSend && !sent && termsMode && (
        <QuoteTermsPicker mode={termsMode} onChange={setTermsMode} disabled={sending} />
      )}

      <div className="mb-5 grid grid-cols-3 gap-3">
        {channels.includes('email') ? (
          <div className={`p-4 border-2 rounded ${sent ? 'border-emerald-300 bg-emerald-50/40' : 'border-sky-300 bg-sky-50/40'}`}
            data-testid="wf-send-card-email">
            <div className="flex items-center gap-2 mb-2">
              <Mail size={14} className="text-sky-600" />
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Email</div>
              {sent && <CheckCircle2 size={14} className="ml-auto text-emerald-500" />}
            </div>
            <div className="text-xs text-slate-700 font-semibold truncate">{client.email || '(no email)'}</div>
            <div className="text-[10px] text-slate-500 mt-1">
              {expandedLetters.length === 0
                ? (childrenLoaded ? '(no letters configured)' : 'Loading quote package…')
                : `${expandedLetters.length} PDF${expandedLetters.length === 1 ? '' : 's'} attached`}
            </div>
          </div>
        ) : <div className="opacity-30 p-4 border-2 border-slate-200 rounded text-xs text-slate-400">Email off</div>}

        {channels.includes('sms') ? (
          <div className={`p-4 border-2 rounded ${sent ? 'border-emerald-300 bg-emerald-50/40' : 'border-purple-300 bg-purple-50/40'}`}
            data-testid="wf-send-card-sms">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare size={14} className="text-purple-600" />
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">SMS</div>
              {sent && <CheckCircle2 size={14} className="ml-auto text-emerald-500" />}
            </div>
            <div className="text-xs text-slate-700 font-semibold truncate">{client.phone_mobile || '(no mobile)'}</div>
            <div className="text-[10px] text-slate-500 mt-1">
              {messageKey ? `Template: ${messageKey}` : 'Single-message confirmation'}
            </div>
          </div>
        ) : <div className="opacity-30 p-4 border-2 border-slate-200 rounded text-xs text-slate-400">SMS off</div>}

        {channels.includes('postal') ? (
          <div className={`p-4 border-2 rounded ${sent ? 'border-emerald-300 bg-emerald-50/40' : 'border-amber-300 bg-amber-50/40'}`}
            data-testid="wf-send-card-postal">
            <div className="flex items-center gap-2 mb-2">
              <Printer size={14} className="text-amber-600" />
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">Postal</div>
              {sent && <CheckCircle2 size={14} className="ml-auto text-emerald-500" />}
            </div>
            <div className="text-xs text-slate-700 space-y-1">
              {expandedLetters.length === 0 && (
                <div className="text-slate-400 italic">{childrenLoaded ? '(no letters configured)' : 'Loading…'}</div>
              )}
              {expandedLetters.map((l, i) => (
                <button
                  key={`${l.key}-${l.appraisal_id_override || 'parent'}-${i}`}
                  onClick={() => downloadPdf(l, false)}
                  data-testid={`wf-send-postal-dl-${l.key}-${i}`}
                  className="block text-sky-700 hover:text-sky-900 underline decoration-dotted text-left w-full"
                >
                  <Download size={11} className="inline mr-1" /> {l.label}
                </button>
              ))}
            </div>
          </div>
        ) : <div className="opacity-30 p-4 border-2 border-slate-200 rounded text-xs text-slate-400">Postal off</div>}
      </div>

      {err && <div className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{err}</div>}

      {/* Batch D · UX polish (Feb 2026): completeness soft-gate.
          Surfaces the deposit state so Trevor sees what letter is
          about to go out — awaiting/received/none — instead of just
          the letter_key on a chip. Not a hard block: the awaiting-
          deposit letter is a legitimate outbound to prompt payment. */}
      {!sent && showAwaitingWarn && (
        <div
          data-testid="wf-send-deposit-awaiting-banner"
          className="mb-4 rounded-md border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
        >
          <span className="mt-0.5 text-lg leading-none text-amber-700">⚠</span>
          <div className="flex-1 text-sm">
            <div className="font-semibold text-amber-900">
              Deposit required but not captured yet
            </div>
            <div className="text-amber-800 mt-0.5">
              This send will use the <code className="px-1 bg-amber-100 rounded text-[11px]">accepted_awaiting_deposit</code> letter to prompt the client for their deposit. If you meant to record the deposit first, go back to <strong>Capture Deposit</strong> — otherwise continue sending.
            </div>
          </div>
        </div>
      )}
      {!sent && showCapturedInfo && (
        <div
          data-testid="wf-send-deposit-received-banner"
          className="mb-4 rounded-md border-2 border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3"
        >
          <CheckCircle2 size={16} className="mt-0.5 text-emerald-700 shrink-0" />
          <div className="flex-1 text-sm">
            <div className="font-semibold text-emerald-900">
              Deposit captured — sending the receipt letter
            </div>
            <div className="text-emerald-800 mt-0.5">
              This send will use <code className="px-1 bg-emerald-100 rounded text-[11px]">accepted_deposit_received</code> to confirm the deposit and next steps.
            </div>
          </div>
        </div>
      )}

      {sent && result && (
        <div className="mb-5 p-4 bg-emerald-50 border-2 border-emerald-300 rounded"
          data-testid="wf-send-confirmation">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-1">
            <CheckCircle2 size={16} /> Sent
          </div>
          <div className="text-xs text-emerald-700">
            {new Date(result.sent_at).toLocaleString()} · channels: {result.channels.join(', ')}
            {result.letter_keys.length > 0 && <> · letters: {result.letter_keys.join(', ')}</>}
            {' '}· stamped <code className="text-[10px] bg-emerald-100 px-1 rounded">{stampField}</code>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-cancel-btn"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        {saveDocConfig.kinds.length > 0 && appraisalId && (
          <SaveDocButton
            appraisalId={appraisalId}
            docKinds={saveDocConfig.kinds}
            groupLabel={saveDocConfig.groupLabel}
            label={saveDocConfig.label}
            variant="outline"
            size="default"
            testId="wf-send-bundle-save-docs-btn"
          />
        )}
        {!sent && (
          <button
            onClick={send}
            disabled={sending || channels.length === 0 || !childrenLoaded || (isQuoteSend && !termsMode)}
            data-testid="wf-send-bundle-send-btn"
            className="inline-flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white font-semibold rounded hover:bg-emerald-700 disabled:opacity-40">
            {sending ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send bundle</>}
          </button>
        )}
        {sent && (
          <button
            onClick={onNext}
            data-testid="wf-send-bundle-continue-btn"
            className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700">
            Continue →
          </button>
        )}
      </div>
    </div>
  );
}
